/**
 * Design store — core design state (items per view, active view, z-index, etc.).
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { DesignItem, DesignViewOutput } from '@core/types';
import { eventBus } from '@core/EventBus';
import { applyFilters } from '@core/VeyoHooks';
import { resolveItemBounds } from '@core/extensionRegistry';
import { useSvgRenderer } from '../composables/useSvgRenderer';
import { migrateItemImageTransform } from '@core/imagePipeline';
import { isViewKey } from '@core/extensionRegistry';
import type { ViewName } from '@core/types';
import { applyPlaceholderAutoFit, isItemLocked, normalizeTemplateItem } from '@core/smartLayout';
import { normalizeSingleLineText } from '../utils/textContent';
import { useUiStore } from './uiStore';

export type { CoreViewName, ViewName } from '@core/types';
export type DesignOrigin = 'new' | 'cart' | 'saved' | 'shared' | 'template';

/**
 * Let addons extend a patch before it lands, so every edit path agrees. Typography Pro uses it to
 * carry a toolbar/Inspector `color` change into its own fill colour, which is what it paints.
 */
function filterItemPatch(item: DesignItem, patch: Partial<DesignItem>): Partial<DesignItem> {
  const draft = { ...patch };
  try {
    const filtered = applyFilters<Partial<DesignItem>>('veyop.item.patch', draft, item);
    return filtered && typeof filtered === 'object' ? { ...filtered } : draft;
  } catch (error) {
    console.warn('[veyop] veyop.item.patch filter failed', error);
    return { ...patch };
  }
}

export const useDesignStore = defineStore('design', () => {
  // ── State ───────────────────────────────────────────────
  const activeView = ref<ViewName>('front');
  // 'front' until the product config lands: a two-side default flashed a Back side on
  // products that only have one.
  const views = ref<ViewName[]>(['front']);
  /** Sides the product itself offers (set once from the product config); null = no limit. */
  const productViews = ref<ViewName[] | null>(null);
  const zIndex = ref(100);
  const designId = ref<string | null>(null);
  const designKey = ref('');
  const designerId = ref('');
  const designFile = ref('');
  const origin = ref<DesignOrigin>('new');

  /** Items per view: { front: [...], back: [...], ... } */
  const items = ref<Record<ViewName, DesignItem[]>>({
    front: [],
    back: [],
    left: [],
    right: [],
  });

  /** Rendered output per view (SVG string, image data) */
  const output = ref<Record<string, DesignViewOutput>>({});

  /** Item list for a view; creates the bucket for addon-registered views on first use. */
  function bucket(view: ViewName): DesignItem[] {
    return (items.value[view] ??= []);
  }

  /** Currently selected item ID */
  const selectedItemId = ref<string | null>(null);

  /** IDs of all items in the current multi-selection */
  const selectedItemIds = ref<string[]>([]);

  /** Snap guides points (percentage or pixels) */
  const snapGuides = ref<{ x: number | null; y: number | null }>({ x: null, y: null });

  // ── Computed ────────────────────────────────────────────
  const activeItems = computed(() => items.value[activeView.value] ?? []);
  const selectedItem = computed(() =>
    activeItems.value.find((i) => i.id === selectedItemId.value) ?? null,
  );
  const hasItems = computed(() =>
    Object.values(items.value).some((list) => list.length > 0),
  );
  const isMultiSelect = computed(() => selectedItemIds.value.length > 1);
  const selectedItems = computed(() =>
    activeItems.value.filter((i) => selectedItemIds.value.includes(i.id)),
  );

  // ── Actions ─────────────────────────────────────────────
  function switchView(view: ViewName) {
    if (activeView.value === view) return;
    activeView.value = view;
    selectedItemId.value = null;
    selectedItemIds.value = [];
    eventBus.emit('change.view.design', { view });
  }

  function addItem(item: DesignItem, view?: ViewName) {
    const v = view ?? activeView.value;
    item.zIndex = ++zIndex.value;
    bucket(v).push(item);
    eventBus.emit('add.item.design', { item });
    selectItem(item.id);
  }

  function removeItem(id: string, view?: ViewName) {
    const v = view ?? activeView.value;
    const list = bucket(v);
    const item = list.find((i) => i.id === id);
    if (item && isItemLocked(item)) return;
    const idx = list.findIndex((i) => i.id === id);
    if (idx !== -1) {
      list.splice(idx, 1);
      if (selectedItemId.value === id) selectedItemId.value = null;
      eventBus.emit('remove.item.design', { id });
    }
  }

  function updateItem(id: string, patch: Partial<DesignItem>, view?: ViewName) {
    const v = view ?? activeView.value;
    const item = bucket(v).find((i) => i.id === id);
    if (item) {
      const nextPatch = filterItemPatch(item, patch);
      const has = (key: string) => Object.prototype.hasOwnProperty.call(nextPatch, key);
      const prevCenterX = Number(item.left ?? 0) + Number(item.width ?? 0) / 2;
      const prevCenterY = Number(item.top ?? 0) + Number(item.height ?? 0) / 2;

      if (has('text')) {
        nextPatch.text = normalizeSingleLineText(nextPatch.text);
      }

      const autoFitPatch = applyPlaceholderAutoFit(item, nextPatch);
      if (autoFitPatch) {
        Object.assign(nextPatch, autoFitPatch);
      }

      Object.assign(item, nextPatch);

      const explicitlySized = has('width') || has('height');
      const shouldAutoFitText = item.type === 'text' && !explicitlySized && (
        has('text') ||
        has('fontSize') ||
        has('fontFamily') ||
        has('fontWeight') ||
        has('fontStyle') ||
        has('lineHeight') ||
        has('letterSpacing') ||
        has('textDecoration') ||
        has('textAlign') ||
        has('textCurve') ||
        has('textCurveReverse') ||
        has('textCurveSpacing')
      );

      if (shouldAutoFitText) {
        const pluginBounds = resolveItemBounds(item);
        if (pluginBounds) {
          applyCenteredBounds(item, prevCenterX, prevCenterY, pluginBounds.width, pluginBounds.height);
        } else {
          const { measureTextLayerBounds } = useSvgRenderer();
          const family = String(item.fontFamily ?? 'Arial');
          const size = Number(item.fontSize ?? 24);
          const weight = String(item.fontWeight ?? 'normal');
          const style = String(item.fontStyle ?? 'normal');
          const lineHeight = Number(item.lineHeight ?? 1.2);
          const letterSpacing = Number(item.letterSpacing ?? 0);
          const textDecoration = String(item.textDecoration ?? 'none');
          const textAlign = String(item.textAlign ?? 'center');
          const txt = normalizeSingleLineText(item.text);
          const measured = measureTextLayerBounds(txt || ' ', family, size, {
            fontWeight: weight,
            fontStyle: style,
            lineHeight,
            letterSpacing,
            textDecoration,
            textAlign,
          });
          applyCenteredBounds(item, prevCenterX, prevCenterY, measured.width, measured.height);
        }
      }

      eventBus.emit('update.design', { property: 'item', value: item });
    }
  }

  function applyCenteredBounds(item: DesignItem, centerX: number, centerY: number, width: number, height: number) {
    const nextW = Math.max(24, Math.ceil(width));
    const nextH = Math.max(24, Math.ceil(height));
    item.width = nextW;
    item.height = nextH;
    item.left = centerX - nextW / 2;
    item.top = centerY - nextH / 2;
  }

  /** Recompute width/height from text/content while preserving the geometric center (e.g. after resize gestures). */
  function refitTextLayerToContent(id: string, view?: ViewName) {
    const v = view ?? activeView.value;
    const item = items.value[v]?.find((i) => i.id === id);
    if (!item || item.type !== 'text') return;

    const centerX = Number(item.left ?? 0) + Number(item.width ?? 0) / 2;
    const centerY = Number(item.top ?? 0) + Number(item.height ?? 0) / 2;

    const pluginBounds = resolveItemBounds(item);
    let w = 0;
    let h = 0;
    if (pluginBounds != null && pluginBounds.width > 0 && pluginBounds.height > 0) {
      w = pluginBounds.width;
      h = pluginBounds.height;
    } else {
      const { measureTextLayerBounds } = useSvgRenderer();
      const family = String(item.fontFamily ?? 'Arial');
      const size = Number(item.fontSize ?? 24);
      const weight = String(item.fontWeight ?? 'normal');
      const style = String(item.fontStyle ?? 'normal');
      const lineHeight = Number(item.lineHeight ?? 1.2);
      const letterSpacing = Number(item.letterSpacing ?? 0);
      const textDecoration = String(item.textDecoration ?? 'none');
      const textAlign = String(item.textAlign ?? 'center');
      const txt = normalizeSingleLineText(item.text);
      const measured = measureTextLayerBounds(txt || ' ', family, size, {
        fontWeight: weight,
        fontStyle: style,
        lineHeight,
        letterSpacing,
        textDecoration,
        textAlign,
      });
      w = measured.width;
      h = measured.height;
    }

    applyCenteredBounds(item, centerX, centerY, w, h);
    eventBus.emit('update.design', { property: 'item', value: item });
  }

  function syncCanvasToolSurface() {
    useUiStore().setCanvasToolSurfaceActive(selectedItemId.value !== null);
  }

  function selectItem(id: string | null) {
    selectedItemId.value = id;
    selectedItemIds.value = id ? [id] : [];
    syncCanvasToolSurface();
    const item = id ? activeItems.value.find((i) => i.id === id) : null;
    if (item) {
      eventBus.emit('select.item.design', { item });
    } else {
      eventBus.emit('deselect.item.design', undefined);
    }
  }

  /** Add/remove item from multi-selection (Shift+click). */
  function toggleSelectItem(id: string) {
    const idx = selectedItemIds.value.indexOf(id);
    if (idx === -1) {
      selectedItemIds.value = [...selectedItemIds.value, id];
    } else {
      selectedItemIds.value = selectedItemIds.value.filter((i) => i !== id);
    }
    selectedItemId.value = selectedItemIds.value[selectedItemIds.value.length - 1] ?? null;
    syncCanvasToolSurface();
  }

  function isItemSelected(id: string): boolean {
    return selectedItemIds.value.includes(id);
  }

  function selectItems(ids: string[]) {
    selectedItemIds.value = ids;
    selectedItemId.value = ids[ids.length - 1] ?? null;
    syncCanvasToolSurface();
  }

  function clearMultiSelect() {
    selectedItemIds.value = [];
    selectedItemId.value = null;
  }

  function clearAll(view?: ViewName) {
    if (view) {
      items.value[view] = [];
    } else {
      for (const v of views.value) items.value[v] = [];
    }
    selectedItemId.value = null;
    selectedItemIds.value = [];
  }

  /**
   * Sides shown for this design. Drafts, templates and saved designs carry the side list they
   * were made on; once the product's sides are known those lists are narrowed to them, so a
   * side the admin never configured (or has since removed) does not come back with them.
   * Items stored for a dropped side stay in `items` but are neither shown nor exported.
   */
  function setAvailableViews(viewNames: ViewName[]) {
    const allowed = productViews.value;
    let next = allowed ? allowed.filter((view) => viewNames.includes(view)) : [...viewNames];
    if (!next.length) next = allowed ? [...allowed] : ['front'];
    views.value = next;
    for (const v of next) bucket(v);
    if (!next.includes(activeView.value)) switchView(next[0]);
  }

  /** The product's own sides (from the catalog config); later side lists are limited to these. */
  function setProductViews(viewNames: ViewName[]) {
    productViews.value = viewNames.length ? [...viewNames] : ['front'];
    setAvailableViews(productViews.value);
  }

  function loadDesignState(state: {
    items?: Partial<Record<ViewName, DesignItem[]>>;
    activeView?: string;
    designId?: string | null;
    designKey?: string;
    designerId?: string;
    designFile?: string;
    origin?: DesignOrigin;
  }) {
    const nextItems = state.items ?? {};
    const hydrateViews = new Set<ViewName>([...views.value, ...Object.keys(nextItems).filter(isViewKey)]);
    for (const view of hydrateViews) {
      items.value[view] = Array.isArray(nextItems[view])
        ? (nextItems[view] as DesignItem[]).map((item) => normalizeTemplateItem(migrateItemImageTransform({ ...item })))
        : [];
    }
    if (state.activeView && views.value.includes(state.activeView as ViewName)) {
      activeView.value = state.activeView as ViewName;
    }
    designId.value = state.designId ?? designId.value;
    if (typeof state.designKey === 'string') designKey.value = state.designKey;
    if (typeof state.designerId === 'string') designerId.value = state.designerId;
    if (typeof state.designFile === 'string') designFile.value = state.designFile;
    if (state.origin) origin.value = state.origin;
    selectedItemId.value = null;
    const maxZ = Math.max(100, ...Object.values(items.value).flat().map((item) => Number(item.zIndex ?? 0)));
    zIndex.value = maxZ;
    eventBus.emit('after.load.design' as string, state);
  }

  function nextId(): string {
    return `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  function nextZIndex(): number {
    return ++zIndex.value;
  }

  // ── Layer ordering ──────────────────────────────────────
  function bringToFront(id: string) {
    const list = bucket(activeView.value);
    const item = list.find((i) => i.id === id);
    if (!item) return;
    const maxZ = Math.max(...list.map((i) => i.zIndex ?? 0));
    item.zIndex = maxZ + 1;
  }

  function bringForward(id: string) {
    const list = bucket(activeView.value);
    const sorted = [...list].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
    const idx = sorted.findIndex((i) => i.id === id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const current = sorted[idx].zIndex ?? 0;
    const above = sorted[idx + 1].zIndex ?? 0;
    sorted[idx].zIndex = above;
    sorted[idx + 1].zIndex = current;
  }

  function sendToBack(id: string) {
    const list = bucket(activeView.value);
    const item = list.find((i) => i.id === id);
    if (!item) return;
    const minZ = Math.min(...list.map((i) => i.zIndex ?? 0));
    item.zIndex = minZ - 1;
  }

  function sendBackward(id: string) {
    const list = bucket(activeView.value);
    const sorted = [...list].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
    const idx = sorted.findIndex((i) => i.id === id);
    if (idx <= 0) return;
    const current = sorted[idx].zIndex ?? 0;
    const below = sorted[idx - 1].zIndex ?? 0;
    sorted[idx].zIndex = below;
    sorted[idx - 1].zIndex = current;
  }

  // ── Style clipboard ─────────────────────────────────────
  const styleClipboard = ref<Record<string, unknown> | null>(null);

  function copyStyles(id: string) {
    const item = activeItems.value.find((i) => i.id === id);
    if (!item) return;
    const { fontFamily, fontSize, fontWeight, fontStyle, textDecoration, textAlign, letterSpacing, color, fill, stroke, opacity, shadow, effects } = item as Record<string, unknown>;
    styleClipboard.value = { fontFamily, fontSize, fontWeight, fontStyle, textDecoration, textAlign, letterSpacing, color, fill, stroke, opacity, shadow, effects };
  }

  function pasteStyles(id: string) {
    if (!styleClipboard.value) return;
    const item = activeItems.value.find((i) => i.id === id);
    if (!item) return;
    Object.assign(item, styleClipboard.value);
    eventBus.emit('update.design', { property: 'item', value: item });
  }

  function reorderItems(fromIndex: number, toIndex: number) {
    const list = bucket(activeView.value);
    if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) return;
    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);
    // Reassign zIndex based on array order
    list.forEach((item, i) => { item.zIndex = 100 + i; });
    zIndex.value = 100 + list.length;
  }

  // ── Layer groups ─────────────────────────────────────────
  const groups = ref<Record<string, string[]>>({}); // groupId → item IDs

  function createGroup(ids: string[]): string {
    if (ids.length < 2) return '';
    const groupId = `group-${Date.now()}`;
    groups.value[groupId] = [...ids];
    for (const id of ids) {
      const item = activeItems.value.find((i) => i.id === id);
      if (item) item.groupId = groupId;
    }
    return groupId;
  }

  function ungroupItems(groupId: string) {
    const ids = groups.value[groupId] ?? [];
    for (const id of ids) {
      const item = activeItems.value.find((i) => i.id === id);
      if (item) delete item.groupId;
    }
    delete groups.value[groupId];
  }

  /** Clone an item (offset slightly, placed on top) and select the copy. Returns the new id. */
  function duplicateItem(id: string, view?: ViewName): string | null {
    const v = view ?? activeView.value;
    const list = items.value[v];
    const source = list.find((i) => i.id === id);
    if (!source) return null;
    const clone = JSON.parse(JSON.stringify(source)) as DesignItem;
    clone.id = nextId();
    clone.zIndex = nextZIndex();
    clone.left = Number(source.left ?? 0) + 12;
    clone.top = Number(source.top ?? 0) + 12;
    delete (clone as Record<string, unknown>).locked;
    list.push(clone);
    eventBus.emit('add.item.design', { item: clone });
    selectItem(clone.id);
    return clone.id;
  }

  return {
    // State
    activeView,
    views,
    productViews,
    zIndex,
    designId,
    designKey,
    designerId,
    designFile,
    origin,
    items,
    output,
    selectedItemId,
    selectedItemIds,
    snapGuides,
    // Computed
    activeItems,
    selectedItem,
    hasItems,
    isMultiSelect,
    selectedItems,
    // Actions
    switchView,
    addItem,
    removeItem,
    updateItem,
    refitTextLayerToContent,
    selectItem,
    toggleSelectItem,
    isItemSelected,
    selectItems,
    clearMultiSelect,
    clearAll,
    setAvailableViews,
    setProductViews,
    loadDesignState,
    nextId,
    nextZIndex,
    bringToFront,
    bringForward,
    sendToBack,
    sendBackward,
    styleClipboard,
    copyStyles,
    pasteStyles,
    reorderItems,
    duplicateItem,
    // Groups
    groups,
    createGroup,
    ungroupItems,
  };
});
